import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import Input, { FieldLabel, FieldGroup } from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { createAdminUser } from '../../utils/api';

const EMPTY = { name: '', username: '', role: 'viewer', password: '' };

export default function ProvisionAccountForm({ onCreated, embedded = false }) {
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event) {
    event.preventDefault();

    const name = form.name.trim();
    const username = form.username.trim().toLowerCase();
    if (!name || !username || !form.password) {
      toast.error('Name, username and password are required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createAdminUser({
        name,
        username,
        role: form.role,
        password: form.password
      });
      toast.success(`Account @${username} provisioned`);
      setForm(EMPTY);
      onCreated?.(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  }

  const formBody = (
    <form className="flex flex-col gap-3.5" onSubmit={onSubmit}>
          <FieldGroup>
            <FieldLabel htmlFor="provision-name">Full name</FieldLabel>
            <Input
              id="provision-name"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. Priya Sharma"
              disabled={submitting}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="provision-username">Username</FieldLabel>
            <Input
              id="provision-username"
              value={form.username}
              onChange={(e) => update('username', e.target.value.toLowerCase())}
              placeholder="e.g. priya"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={submitting}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="provision-role">Role</FieldLabel>
            <Select
              id="provision-role"
              value={form.role}
              onChange={(e) => update('role', e.target.value)}
              disabled={submitting}
            >
              <option value="viewer">Viewer — read only</option>
              <option value="admin">Admin — can edit</option>
            </Select>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="provision-password">Temporary password</FieldLabel>
            <Input
              id="provision-password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              disabled={submitting}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
          </FieldGroup>

          <Button type="submit" loading={submitting} leftIcon={UserPlus} className="mt-1">
            Create account
          </Button>
        </form>
  );

  if (embedded) return formBody;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-col items-stretch gap-1">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-brand-50 text-brand-600 inline-flex items-center justify-center">
            <UserPlus className="h-4 w-4" />
          </span>
          <CardTitle>Provision new account</CardTitle>
        </div>
        <CardDescription>
          Create an admin or viewer. Super admins cannot be created here.
        </CardDescription>
      </CardHeader>
      <CardContent>{formBody}</CardContent>
    </Card>
  );
}
